/**
 * @file CompanyFeaturesPage.tsx
 * @brief CompanyAdmin Features — turn products on/off for employees.
 *
 * @date 15-07-2026
 * @updated 09-09-2026 — platform lock banner for Medical Assessment
 */

import { SkeletonRows } from "@/components/ui/Skeleton";
import type { ReactNode } from "react";
import { Languages, Link2, LoaderCircle, Sparkles, PenLine, Zap } from "lucide-react";
import { BoardroomTable } from "@/components/icons/BoardroomTable";
import { MedicalReport } from "@/components/icons/MedicalReport";
import { Switch } from "@/components/ui/Switch";
import {
  GridBand,
  GridBlock,
  GridCell,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import {
  useCompanyFeatures,
  useUpdateCompanyFeatures,
  type FeatureAccess,
} from "./api";
import { MAKE_SWITCH_TEXT, ZAPIER_SWITCH_TEXT } from "@/features/integrations/zapierCopy";
import { MakeLogo } from "@/features/integrations/MakeLogo";
import { ZapierWordmark } from "@/features/integrations/ZapierWordmark";

type FeatureMeta = {
  key: keyof FeatureAccess;
  label: string;
  description: string;
  icon: typeof Languages;
  /** W31/M1: the partner's own logo shown instead of `label` (Zapier, Make). */
  logo?: ReactNode;
};

const FEATURE_META: FeatureMeta[] = [
  {
    key: "interpretation",
    label: "Interpretation",
    description: "Live speech-to-speech translation in the app.",
    icon: Languages,
  },
  {
    key: "notes",
    label: "Personal Notes",
    description: "Voice notes with AI transcription and insights.",
    icon: PenLine, // DES 08-10-2026: app icon, was StickyNote
  },
  {
    key: "boardroom",
    label: "Boardroom / Meeting Intel",
    description:
      "Meeting recording, transcripts, summaries and action items.",
    icon: BoardroomTable, // DES 08-10-2026: app icon, was Presentation
  },
  {
    key: "medical_assessment",
    label: "Medical Assessment",
    description:
      "Medico-legal assessments, clinic-software statements, and MLR report workflow.",
    icon: MedicalReport, // DES 08-10-2026: app icon, was FileHeart
  },
  {
    // MCP Phase 2 (06-10-2026). Binds admins too: off means no one in the
    // company can let Claude / ChatGPT read company meetings.
    key: "ai_connectors",
    label: "AI assistant connectors",
    description:
      "Lets people connect Claude, ChatGPT and other AI apps to read the meetings they can open. Read-only. Off stops every connected app at once, admins included.",
    icon: Sparkles,
  },
  {
    // ADR-051 (21-10-2026). Binds admins too while inside-only sharing is on.
    key: "asana",
    label: "Asana",
    description:
      "Send meeting to-dos to Asana. Needed while \"Only share inside the company\" is on (Company settings, Data & sharing); it then applies to admins too.",
    icon: Link2,
  },
  {
    // Z1 (27-10-2026, ADR-060). Opt-in and binds admins too, whatever
    // "Only share inside the company" says: Zapier passes data on to other apps.
    key: "zapier",
    label: "Zapier",
    description: ZAPIER_SWITCH_TEXT,
    icon: Zap,
    logo: <ZapierWordmark height={18} />,
  },
  {
    // M1 (29-10-2026). Its own switch, same rules as zapier: allowing Zapier
    // does not allow Make (another processor).
    key: "make",
    label: "Make",
    description: MAKE_SWITCH_TEXT,
    icon: Zap,
    logo: <MakeLogo height={16} />,
  },
];

export function CompanyFeaturesPage() {
  const { data, isLoading, isError } = useCompanyFeatures();
  const update = useUpdateCompanyFeatures();

  const features = data?.featureAccess;
  const platformLocks = data?.platformLocks ?? {};

  const pendingKey = update.isPending
    ? (Object.keys(update.variables ?? {})[0] as keyof FeatureAccess | undefined)
    : undefined;

  const handleToggle = async (meta: FeatureMeta, next: boolean) => {
    try {
      await update.mutateAsync({ [meta.key]: next });
      toast({
        variant: "success",
        title: next ? `${meta.label} enabled` : `${meta.label} disabled`,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Update failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <GridBand>
          <h2 className="text-xl font-semibold tracking-tight">Features</h2>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            Turn products on or off for your team. Disabled features are hidden
            in your employees&apos; apps. Admins keep full access (AI assistant
            connectors, Zapier and Make aside), and
            existing data is never deleted — re-enabling restores everything.
            Products locked by Verblizr cannot be enabled here.
          </p>
        </GridBand>

        <GridBand className="text-sm font-medium">
          Employee access
          <span className="ml-1 font-normal text-muted-foreground">
            · Applies to regular employees. You and other admins are unaffected
            unless a product is locked platform-wide, except AI assistant
            connectors, Zapier and Make, which apply to everyone in the company.
          </span>
        </GridBand>

        <GridCell padded={false}>
          {isLoading ? (
            <SkeletonRows rows={6} />
          ) : isError || !features ? (
            <div className="px-4 py-10 text-sm text-destructive md:px-5">
              Couldn&apos;t load feature access. Please refresh and try again.
            </div>
          ) : (
            <ul className="divide-y divide-border/60">
              {FEATURE_META.filter((meta) => features[meta.key] !== undefined).map((meta) => {
                const Icon = meta.icon;
                const enabled = features[meta.key] === true;
                const platformLocked = platformLocks[meta.key] === true;
                const rowPending = pendingKey === meta.key;
                return (
                  <li
                    key={meta.key}
                    className="flex items-center justify-between gap-4 px-4 py-4 md:px-5"
                  >
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 bg-muted p-2 text-muted-foreground">
                        <Icon className="h-4 w-4" />
                      </span>
                      <div>
                        <div className="flex items-center gap-2 font-medium">
                          {meta.logo ?? meta.label}
                          {rowPending ? (
                            <LoaderCircle className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                          ) : null}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {platformLocked
                            ? "Locked off by Verblizr platform settings — not available yet."
                            : meta.description}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={enabled}
                      disabled={update.isPending || platformLocked}
                      onCheckedChange={(next) => void handleToggle(meta, next)}
                      aria-label={`${meta.label} ${enabled ? "enabled" : "disabled"}`}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </GridCell>
      </GridBlock>
    </div>
  );
}
